import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Flex,
  Form,
  FormListFieldData,
  InputNumber,
  Table,
  TableColumnsType
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useState } from 'react'

import {
  ClassifierCategory,
  ClassifierType,
  DocumentServiceDto,
  InventoryDocumentResponse2
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  EditableFormItem,
  SubmitButtons,
  Text
} from 'src/components'
import { fixedDecimal } from 'src/features/stock/utils'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  item?: InventoryDocumentResponse2
  fields: FormListFieldData[]
  form?: FormInstance
  add: () => void
  remove: (index: number) => void
}

const fieldsToReset = ['serviceId', 'serviceName', 'unitPrice']

const generateFields = (services: { name?: string }[], index?: number) => {
  const targets =
    typeof index === 'number' ? [index] : services.map((_, i) => i)

  return targets
    .map((i) => fieldsToReset.map((field) => ['services', i, field]))
    .flat()
}

export const ServicesTable = ({ fields, form, item, add, remove }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const palette = useTheme()

  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number>()

  const serviceId = useClassifierId(
    ClassifierType.STOCK_PRODUCT_SERVICE,
    ClassifierCategory.STOCKS
  )

  const contractId = Form.useWatch('contractId', form)

  const onSave = async () => {
    const services = form?.getFieldValue('services')

    if (typeof editingIndex !== 'number') return

    const fieldsToValidate = generateFields(services, editingIndex)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      const updatedRow = values.services?.[editingIndex]

      const updatedServices = [...services]
      updatedServices[editingIndex] = {
        ...updatedServices[editingIndex],
        ...updatedRow
      }

      form?.setFieldsValue({ services: updatedServices })
      setIsNewConsumable(false)
      setEditingIndex(undefined)
    }
  }

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(fieldsToReset.map((field) => ['stock', index, field]))
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onDelete = (index: number) => {
    remove(index)
  }

  const columns: TableColumnsType = [
    {
      key: 'serviceName',
      width: '20%',
      title: t('stock.service_name'),
      render: (_, __, index) => {
        return item ? (
          <Text weight={400} color={palette.blue6}>
            {form?.getFieldValue(['services', index, 'serviceName'])}
          </Text>
        ) : (
          <EditableFormItem
            name={[index, 'serviceName']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <ClassifierInfiniteSelect
              id={serviceId}
              placeholder={t('stock.service_name_select')}
              onSelect={(value, option) => {
                const services = form?.getFieldValue('services') || []

                const updatedServices = services.map(
                  (service: DocumentServiceDto, i: number) => {
                    if (i !== editingIndex) return service

                    return {
                      ...service,
                      serviceId: value,
                      serviceName: option.label
                    }
                  }
                )

                form?.setFieldsValue({ services: updatedServices })
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'unitPrice',
      align: 'center',
      width: '10%',
      title: t('stock.service_price'),
      render: (_, __, index) => (
        <EditableFormItem
          name={[index, 'unitPrice']}
          isEditing={editingIndex === index}
          rules={requiredRules}
        >
          <InputNumber<number>
            placeholder={t('common.quantity')}
            className="full-width"
            min={0}
            onChange={(value) => {
              const services = form?.getFieldValue('services') || []

              const updatedServices = services.map(
                (service: DocumentServiceDto, i: number) => {
                  if (i !== editingIndex) return service

                  const unitPriceFixed = fixedDecimal(value ?? 0, 4)

                  return {
                    ...service,
                    unitPrice: unitPriceFixed
                  }
                }
              )

              form?.setFieldsValue({ services: updatedServices })
            }}
          />
        </EditableFormItem>
      )
    },
    ...(!item
      ? [
          {
            width: '10%',
            key: 'actions',
            fixed: 'right' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => onDelete(index)}
              />
            )
          }
        ]
      : [])
  ]

  const addConsumable = async () => {
    const services = form?.getFieldValue('services')

    if (services?.length > 0) {
      const fieldsToValidate = generateFields(services)

      await form?.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewConsumable(true)
  }

  return (
    <Table
      className={styles.table}
      id="scrollableTable"
      dataSource={fields}
      bordered
      pagination={false}
      scroll={{ x: 'max-content' }}
      columns={columns}
      footer={() =>
        !item && (
          <Flex justify="center">
            <Button
              onClick={addConsumable}
              icon={<PlusOutlined />}
              disabled={!contractId}
            >
              {t('stock.services_add')}
            </Button>
          </Flex>
        )
      }
    />
  )
}
